import Cookie from 'js-cookie';

import { API2_HOST } from '@/utils/constants';
import { isUnAuthPage } from '@/utils/loginAuth';
import redirectUrlMap from '@/utils/redirectUrlMap';
import { $tracing } from '@/utils/tracing';

import {
  BYBIT_LANG_KEY,
  BYBIT_TOKEN_COOKIE_KEY,
  I18N_URL_REGEX,
} from '../utils/constants';
import { getGuid } from '../utils/guid';
const tracingApi = '/v2/public/log'; // 日志上报接口
const rtTracingApi = '/v2/public/metrics'; // 日志上报接口

interface IRequestOption<T> {
  // eslint-disable-next-line @typescript-eslint/ban-types
  headers?: {};
  method?:
    | 'GET'
    | 'POST'
    | 'PUT'
    | 'DELETE'
    | 'get'
    | 'post'
    | 'put'
    | 'delete';
  body?: any;
  responseType?: (typeof validResponseType)[number];
  errorHandler?: (response: Response) => void;
  hideError?: boolean;
  flag?: string;
  deleteContentType?: boolean;
}

class ByError extends Error {
  data: {
    ret_code: number | string;
    ret_msg: string;
    ext_info: null | string;
    ext_code: number | string;
    result?: any;
  } | null = null;
}

const validResponseType = [
  'text',
  'json',
  'blob',
  'arrayBuffer',
  'formData',
] as const;

const defaultErrorHandler = (ret: unknown) => {
  console.log('defaultErrorHandler:', ret);
};
const defaultOption = {
  'Content-Type': 'application/json;charset=UTF-8',
  Accept: 'application/json',
  platform: 'pc',
};

// 是否上报
function isReport(url: any) {
  return url.indexOf(tracingApi) === -1 && url.indexOf(rtTracingApi) === -1;
}
// 调用上报接口
function report(
  obj: any = {},
  method: string,
  timestamp: number,
  isReportTracing = false,
) {
  const { status, url } = obj;
  const responseEndTime = new Date().getTime();
  const elapse = responseEndTime - timestamp;
  if (isReport(url)) {
    const path = url.replace(API2_HOST, '');
    const parms = ['path', path];
    if (status !== 200) {
      parms.concat(['err', '1']);
    }
    if (isReportTracing) {
      const { response } = obj;
      $tracing.addEvent({
        r: path,
        t: 'err',
        c: response ? response.status : -1,
        m: obj.message,
        ext: { method },
      });
    }
  }
}

const fetchData = <T>(
  url: string,
  options: IRequestOption<T> = { headers: {} },
): Promise<T> => {
  let fetchUrl = url;
  if (options.body && (!options.method || options.method === 'GET')) {
    const bodyData = JSON.parse(options.body);
    const queryParams = Object.keys(bodyData).map(
      (key) => `${key}=${encodeURIComponent(bodyData[key])}`,
    );

    fetchUrl = `${url}?${queryParams.join('&')}`;

    delete options.body;
  }
  // const userToken = Cookie.get('b_t_c_k_cht-test-6');
  const userToken = Cookie.get(BYBIT_TOKEN_COOKIE_KEY);
  const guid = getGuid();
  const pathname = window.location.pathname;

  const matched = pathname.match(I18N_URL_REGEX);

  const lang = matched?.[1]
    ? matched?.[1]
    : localStorage.getItem(BYBIT_LANG_KEY)
    ? localStorage.getItem(BYBIT_LANG_KEY)
    : 'en';
  const mergedOptions = {
    credentials: 'include',
    ...options,
    headers: {
      ...defaultOption,
      UserToken: (userToken === 'null' ? '' : userToken) || '',
      Guid: guid,
      Lang: lang === 'en-US' ? 'en' : lang,
      ...options.headers,
    },
  } as any;
  let currentSite = '';

  return new Promise((resolve, reject) => {
    if (mergedOptions.deleteContentType)
      delete mergedOptions.headers['Content-Type'];
    // const method = mergedOptions.method?.toLowerCase() ?? 'get';
    const timestamp = new Date().getTime();
    fetch(fetchUrl, mergedOptions)
      .then((resp: any) => {
        const { errorHandler } = options;
        if (resp.status >= 400) {
          report(resp, options.method as string, timestamp, false);
          const handler = errorHandler || defaultErrorHandler;
          handler(resp);
          reject();
          return null;
        }

        if (resp.status === 200) {
          report(resp, options.method as string, timestamp, false);
        }
        currentSite = resp?.headers.get('x-refer-site-id');

        return resp.json();
      })
      .then((data: any) => {
        const { ext_info, ext_code, result, ret_code, ret_msg } = data || {};

        if (!(ret_code === undefined) && ret_code !== 0) {
          const error = new ByError(ret_msg);
          error.data = { ret_code, ret_msg, ext_info, ext_code, result };
          throw error;
        }
        resolve({ ...data, currentSite });
      })
      .catch((e: ByError) => {
        if (!e || !e.data) {
          reject({ ret_code: -200, ret_msg: e, poz: 2 });
          return;
        }
        const { ret_code, ret_msg, ext_info, ext_code, result } = e.data;

        if (ret_code === 10007 || ret_code === 33003 || ret_code === 10009) {
          if (!isUnAuthPage()) {
            if (window.location.pathname.indexOf('/notifications') > -1) {
              const redirectUrl = `?redirect_url=${encodeURIComponent(
                window.location.href,
              )}`;
              redirectUrlMap.redirectLogin(redirectUrl);
            } else {
              redirectUrlMap.redirectLogin();
            }
            reject(e.data);
            return;
          }
        }
        if (ret_code === 10008 || ret_code === 31003) {
          reject(e.data);
        }
        if (ret_code === 31019) {
          reject({ ret_code, ret_msg, ext_info, ext_code });
        }
        reject({ ret_code, ret_msg, ext_info, result });
        report(
          { status: undefined, url: fetchUrl },
          options.method as string,
          timestamp,
          false,
        );
        // return new Promise(() => reject({ ret_code, ret_msg, ext_info }));
      });
  });
};

fetchData.get = <T>(url: string, options?: any) =>
  fetchData<T>(url, {
    ...options,
    method: 'GET',
  });

fetchData.post = <T>(url: string, options?: any) =>
  fetchData<T>(url, {
    ...options,
    method: 'POST',
  });

fetchData.put = (url: string, options?: any) =>
  fetchData(url, {
    ...options,
    method: 'PUT',
  });

fetchData.delete = (url: string, options?: any) =>
  fetchData(url, {
    ...options,
    method: 'DELETE',
  });

export default fetchData;
